import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Loader2, User, MessageCircle, Send } from 'lucide-react';
import { Layout } from './Home';
import PageHero from '../components/PageHero';
import HtmlContent from '../components/HtmlContent';
import { getPersonBlogPost, resolveFileUrl } from '../services/api';
import { listPublicComments, postPublicComment } from '../services/authorApi';
import { toast } from '../hooks/use-toast';

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const BlogPostPage = () => {
  const { slug: personSlug, postSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getPersonBlogPost(personSlug, postSlug)
      .then(setData)
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [personSlug, postSlug]);

  if (notFound) return <Navigate to={`/people/${personSlug}`} replace />;

  return (
    <Layout>
      {loading || !data ? (
        <div className="bg-[#e2ede6] py-32 text-center text-[#0b1a2e]/60">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <>
          <PageHero
            eyebrow={`Blog · ${formatDate(data.post.published_at)}`}
            title={data.post.title}
            description={data.post.excerpt}
            image={
              resolveFileUrl(data.post.cover_image) ||
              'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1600&q=80'
            }
          />
          <section className="bg-[#e2ede6] py-16 lg:py-24" data-testid="blog-post-page">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
              <div className="mb-8 flex items-center gap-4 bg-white rounded-2xl border border-black/5 p-4 shadow-sm">
                <div className="w-14 h-14 rounded-full overflow-hidden bg-[#e2ede6] shrink-0">
                  {data.person.photo_url ? (
                    <img src={resolveFileUrl(data.person.photo_url)} alt={data.person.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#0b1a2e]/30"><User className="w-6 h-6" /></div>
                  )}
                </div>
                <div className="min-w-0">
                  <Link to={`/people/${data.person.slug}`} className="font-semibold text-[#0b1a2e] hover:text-[#0f5c3d] transition-colors">
                    {data.person.name}
                  </Link>
                  <div className="text-xs text-[#0b1a2e]/60">{data.person.designation}</div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-black/5 p-6 lg:p-10 shadow-sm">
                <HtmlContent html={data.post.content_html} />
              </div>

              <div className="mt-8 text-center">
                <Link
                  to={`/people/${data.person.slug}`}
                  data-testid="back-to-person"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#0f5c3d]/25 text-[#0b1a2e] text-sm hover:bg-white transition"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to {data.person.name.split(' ')[0]}'s profile
                </Link>
              </div>

              <CommentsSection personSlug={data.person.slug} postSlug={data.post.slug} authorName={data.person.name} />
            </div>
          </section>
        </>
      )}
    </Layout>
  );
};

export default BlogPostPage;

// ─── Comments section ─────────────────────────────────────────

const CommentsSection = ({ personSlug, postSlug, authorName }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ author_name: '', author_email: '', body: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    listPublicComments(personSlug, postSlug)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [personSlug, postSlug]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.body.trim().length < 2) return;
    setBusy(true);
    try {
      const r = await postPublicComment(personSlug, postSlug, form);
      toast({ title: 'Thanks!', description: r.message || 'Your comment is awaiting review.' });
      setForm({ author_name: '', author_email: '', body: '' });
    } catch (err) {
      toast({ title: 'Failed to post', description: err.response?.data?.detail || String(err), variant: 'destructive' });
    } finally { setBusy(false); }
  };

  // build a shallow tree: top-level comments + replies keyed by parent_id
  const roots = items.filter((c) => !c.parent_id);
  const repliesBy = items.filter((c) => c.parent_id).reduce((m, c) => {
    (m[c.parent_id] = m[c.parent_id] || []).push(c);
    return m;
  }, {});

  return (
    <div className="mt-12 pt-10 border-t border-black/10" data-testid="comments-section">
      <div className="flex items-center gap-2 mb-6">
        <MessageCircle className="w-5 h-5 text-[#0f5c3d]" />
        <h3 className="font-serif text-2xl text-[#0b1a2e]">Comments <span className="text-sm text-[#0b1a2e]/50">({roots.length})</span></h3>
      </div>

      {loading ? (
        <div className="text-center text-[#0b1a2e]/50 py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : roots.length === 0 ? (
        <p className="text-sm text-[#0b1a2e]/60 mb-8">Be the first to leave a comment.</p>
      ) : (
        <div className="space-y-6 mb-10">
          {roots.map((c) => (
            <div key={c.id} className="bg-white rounded-xl border border-black/5 p-5" data-testid="public-comment">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-sm text-[#0b1a2e]">{c.author_name}</span>
                <span className="text-xs text-[#0b1a2e]/50">· {new Date(c.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-sm text-[#0b1a2e] whitespace-pre-wrap">{c.body}</p>
              {(repliesBy[c.id] || []).map((r) => (
                <div key={r.id} className="mt-4 ml-4 pl-4 border-l-2 border-[#0f5c3d]/30 bg-[#f7fbf8] rounded-r-lg p-3" data-testid="author-reply">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-[#0f5c3d]">{r.author_name}</span>
                    <span className="text-[10px] bg-[#0f5c3d] text-white px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider">Author</span>
                    <span className="text-xs text-[#0b1a2e]/50">· {new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-[#0b1a2e] whitespace-pre-wrap">{r.body}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="bg-white rounded-xl border border-black/5 p-5 space-y-3" data-testid="comment-form">
        <h4 className="font-semibold text-[#0b1a2e]">Leave a comment for {authorName}</h4>
        <p className="text-xs text-[#0b1a2e]/60">All comments are reviewed before publication.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input required minLength={2} maxLength={80} value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} placeholder="Your name" data-testid="comment-name" className="border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
          <input required type="email" value={form.author_email} onChange={(e) => setForm({ ...form, author_email: e.target.value })} placeholder="Email (not published)" data-testid="comment-email" className="border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm" />
        </div>
        <textarea required minLength={2} maxLength={4000} rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Your comment…" data-testid="comment-body" className="w-full border border-[#0f5c3d]/15 rounded-lg px-3 py-2 text-sm resize-y" />
        <button type="submit" disabled={busy} data-testid="comment-submit" className="inline-flex items-center gap-1.5 bg-[#0f5c3d] text-white rounded-full px-5 py-2 text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post comment
        </button>
      </form>
    </div>
  );
};
